import React, { useEffect, useState } from 'react';
import { getAllAuctionsAPI } from '../../apis/auction';
import dayjs from 'dayjs';
import classNames from 'classnames';
import { Layout, Menu, message, Modal, Spin, Input } from 'antd';
import TennisUser from '../tennisUser';
import Loading from '../SearchTennis/loader';
import { useNavigate } from 'react-router-dom';
import './index.scss';
import { getPaymentAccountAPI, sendCheckPaymentStatusAPI } from '../../apis/payment';
import { insertTennisOrderAPI } from '../../apis/auction';
import i18n from '../../utils/i18n';
import { useTranslation } from 'react-i18next';

export default function Auction() {
    const { t } = useTranslation();
    const [auctions, setAuctions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    // Modal / QR 状态
    const [qrModalVisible, setQrModalVisible] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [qrUrl, setQrUrl] = useState<string | null>(null);
    const [currentAuction, setCurrentAuction] = useState<any | null>(null);

    // 新增：PayPay 账号输入 modal 状态
    const [showPaypayAccountModal, setShowPaypayAccountModal] = useState(false);
    const [paypayAccount, setPaypayAccount] = useState<string>('');
    const [sending, setSending] = useState(false);
    const [buyerEmail, setBuyerEmail] = useState<string>('');

    async function fetchSellerQr(sellerId: number | string) {
        // 直接返回静态二维码图片路径（无需后端请求）
        return '/uploads/qrcode/0130.jpg';
    }
    async function handleBuyerPaid(paypayAccountInput?: string) {
        if (!currentAuction) return;
        const userId = localStorage.getItem('userId');
        if (!userId) {
            message.warning(t('auction.loginRequired'));
            return;
        }

        const recipientEmail = 'ganjiabubuzhang@gmail.com';

        if (!recipientEmail) {
            message.warning(t('auction.noRecipientEmail'));
            return;
        }

        const payAccount = paypayAccountInput ?? paypayAccount;

        // 先创建 tennis_orders，如果失败提示重试；成功再发送确认邮件
        const orderPayload = {
            auction_id: Number(currentAuction.id),
            buyer_id: Number(userId),
            price: Number(currentAuction.price ?? 0),
            paypay_account: payAccount ?? null,
            buyer_email: buyerEmail ?? null,
            paypay_qr_url: qrUrl ?? null,
            lang: (i18n.language === 'zh' || i18n.language === 'ja') ? i18n.language : 'ja',
            status: 'NotSent',
        };

        const params = {
            email: recipientEmail,
            venueName: currentAuction.court_name ?? '',
            date: (currentAuction.DATE || currentAuction.date) ? dayjs(currentAuction.DATE || currentAuction.date).format('YYYY-MM-DD') : '',
            timeRange: currentAuction.time_range ?? '',
            price: currentAuction.price ?? 0,
            paypayAccount: payAccount ?? '',
            buyerName: localStorage.getItem('uname') || undefined,
            buyerEmail: buyerEmail || '',
            orderId: currentAuction.id ? String(currentAuction.id) : undefined,
            lang: (i18n.language === 'zh' || i18n.language === 'ja') ? i18n.language : 'ja',
        };

        setSending(true);
        try {
            // 1) 创建订单
            console.log('creating tennis order with payload:', orderPayload);
            const orderResp = await insertTennisOrderAPI(orderPayload);
            console.log('insertTennisOrderAPI resp:', orderResp);
            if (!orderResp || orderResp.success !== true) {
                // 后端返回失败或格式不对
                message.error(t('auction.createOrderFailed'));
                return;
            }

            console.log('sending payment-check mail params:', params);
            const resp = await sendCheckPaymentStatusAPI(params);
            console.log('sendCheckPaymentStatusAPI resp:', resp);
            message.success(t('auction.paymentMailSent'));
            setQrModalVisible(false);
            // 刷新拍卖列表（更新页面中的可售项）
            try {
                const refreshed = await getAllAuctionsAPI();
                const list = refreshed?.result?.auctions ?? [];
                setAuctions(list);
            } catch (refreshErr) {
                console.error('refresh auctions error:', refreshErr);
            }
        } catch (err) {
            console.error('sendCheckPaymentStatus error:', err);
            message.error(t('auction.paymentMailFailed'));
        } finally {
            setSending(false);
            setTimeout(() => setShowPaypayAccountModal(false), 50);
        }
    }

    // 购买处理：先检查是否登录（localStorage 中是否有 userId），未登录则提示
    async function handleBuy(item: any) {
        const userId = localStorage.getItem('userId');
        if (!userId) {
            message.warning(t('auction.loginRequired'));
            return;
        }
        setCurrentAuction(item);
        setQrModalVisible(true);
        setModalLoading(true);
        setQrUrl(null);
        try {
            const url = await fetchSellerQr(item.seller);
            if (!url) {
                message.error(t('auction.fetchQrFailed'));
            }
            setQrUrl(url);
        } catch (err) {
            console.error(err);
            message.error(t('auction.fetchQrFailed'));
        } finally {
            setModalLoading(false);
        }
    }

    // 在打开「已付款」前检查是否已有保存的 PayPay 账号，并预填到输入框
    async function handleOpenPaypayModal() {
        const userId = localStorage.getItem('userId');

        setModalLoading(true);
        try {
            const resp = await getPaymentAccountAPI(userId);
            console.log('getPaymentAccountAPI response:', resp);
            // 兼容不同 request 返回结构（axios 返回在 resp.data）
            const body = resp?.data ?? resp;
            if (body) {
                const account = body.paypay_account ?? '';
                const mail = body.mailAddress ?? body.email ?? '';
                console.log('Loaded PayPay account:', account, 'mailAddress:', mail);
                setPaypayAccount(account);
                // 保存买家邮箱供后续发送 API 使用
                setBuyerEmail(mail);
                if (account) {
                    message.success('既存の PayPay アカウントを読み込みました');
                }
            } else {
                // 没有数据或未找到用户时，清空输入，允许手动填写
                setPaypayAccount('');
            }
        } catch (err) {
            console.error('getPaymentAccount error:', err);
            message.warning('PayPay アカウントの読み込みに失敗しました。手動で入力してください。');
            setPaypayAccount('');
        } finally {
            setModalLoading(false);
            setShowPaypayAccountModal(true);
        }
    }

    // const ok = window.confirm(`「${item.court_name}」を購入しますか？\n${item.DATE || ''} ${item.time_range || ''}\n価格: ${item.price} 円`);
    // if (!ok) return;
    // try {
    //     const resp = await fetch('/api/buy-auction', {
    //         method: 'POST',
    //         headers: { 'Content-Type': 'application/json' },
    //         body: JSON.stringify({ auctionId: item.id, buyerId: Number(userId) }),
    //     });
    //     if (resp.ok) {
    //         message.success('購入リクエストを送信しました');
    //         // 刷新列表
    //         const res = await getAllAuctionsAPI();
    //         setAuctions(res?.result?.auctions ?? []);
    //     } else {
    //         const data = await resp.json().catch(() => ({}));
    //         console.error('buy error:', data);
    //         message.error('購入に失敗しました');
    //     }
    // } catch (err) {
    //     console.error('buy error:', err);
    //     message.error('購入処理中にエラーが発生しました');
    // }

    useEffect(() => {
        let mounted = true;
        const fetch = async () => {
            setLoading(true);
            try {

                const res = await getAllAuctionsAPI();
                const list = res?.result?.auctions ?? [];
                if (mounted) setAuctions(list);
            } catch (err) {
                console.error('fetch auctions error:', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetch();
        return () => { mounted = false; };
    }, []);

    const { Header } = Layout;

    return (
        <div className="auctionPage" style={{ padding: 24 }}>
            <Layout>
                <Header style={{ background: '#fff', padding: 0, width: '100%' }}>
                    <Menu mode="horizontal" defaultSelectedKeys={['auction']}>
                        <Menu.Item
                            key="loading"
                            className='loading-menu-item'
                            onClick={() => navigate('/searchTennis')}
                        >
                            <Loading />
                        </Menu.Item>
                        <Menu.Item
                            key="auction"
                            className='auction-menu-item'
                        >
                            <span>{t('auction.menuTitle')}</span>
                            {loading && <span style={{ marginLeft: 16 }}>{t('auction.loading')}</span>}
                        </Menu.Item>
                    </Menu>
                    <div className="user-info">
                        <TennisUser />
                    </div>
                </Header>
            </Layout>
            {/* QR Modal */}
            <Modal
                className='paypay-qr-modal'
                title={t('auction.enterPaypayAccount')}
                open={qrModalVisible}
                onCancel={() => setQrModalVisible(false)}
                onOk={handleOpenPaypayModal}
                okText={t('auction.modalOkPaid')}
                cancelText={t('auction.modalCancel')}
            >
                {/* 購入情報をモーダル上部に表示 */}
                {currentAuction && (
                    <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>{currentAuction.court_name}</div>
                        <div style={{ color: '#666', marginTop: 6 }}>
                            {(currentAuction.DATE || currentAuction.date) ? dayjs(currentAuction.DATE || currentAuction.date).format('YYYY-MM-DD') : ''}
                            &nbsp;{currentAuction.time_range ?? ''}
                        </div>
                        <div style={{ marginTop: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ color: '#444' }}>{currentAuction.court_type ?? ''}</div>
                            <div className="goods-price-count" style={{ marginLeft: 12 }}>
                                <div className="goods-price">
                                    {Number(currentAuction.price || 0).toLocaleString()}
                                    <span className="goods-price-unit">&nbsp;円</span>
                                </div>
                            </div>
                        </div>
                        <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                            出品者: {currentAuction.seller_uname ?? '-'}
                        </div>
                    </div>
                )}
                {modalLoading ? (
                    <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
                ) : qrUrl ? (
                    <div style={{ textAlign: 'center' }}>
                        <img src={qrUrl} alt="PayPay QR" style={{ maxWidth: '100%', height: 'auto' }} />
                        <div style={{ marginTop: 8, color: '#666' }}>{t('auction.payAfterClick')}</div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', color: '#999' }}>{t('auction.qrMissing')}</div>
                )}
            </Modal>
            {/* 输入 PayPay 账号的第二个 Modal */}
            <Modal
                title={t('auction.enterPaypayAccount')}
                open={showPaypayAccountModal}
                onCancel={() => { setShowPaypayAccountModal(false); setPaypayAccount(''); }}
                onOk={async () => {
                    if (!paypayAccount) {
                        message.warning(t('auction.enterPaypayAccount'));
                        return;
                    }
                    // 等待 handleBuyerPaid 完成后再关闭 modal
                    await handleBuyerPaid(paypayAccount);
                    console.log('test1:', showPaypayAccountModal);
                    console.log('test2:', sending);
                }}
                confirmLoading={sending}
                okText={t('auction.modalOkInput')}
                cancelText={t('auction.modalCancel')}
            >
                <div style={{ marginTop: 8 }}>
                    <Input
                        placeholder={t('auction.paypayPlaceholder')}
                        value={paypayAccount}
                        onChange={e => setPaypayAccount(e.target.value)}
                    />
                </div>
            </Modal>

            {loading ? (
                <p>{t('auction.loading')}</p>
            ) : auctions.length === 0 ? (
                <p>{t('auction.noAvailable')}</p>
            ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                    {auctions.map((a: any) => {
                        const imgSrc = a.image || a.metadata?.cover || '/uploads/default/court.png';
                        const rawDate = a.DATE || a.date || a.Date || '';
                        // 规范化日期显示
                        let formattedDate = rawDate ? dayjs(rawDate).format('YYYY-MM-DD') : '';
                        let timeRange = a.time_range || a.timeRange || '';
                        // 状态映射（英文枚举 -> 日语表示）
                        const statusKey = String(a.STATUS ?? a.status ?? 'active');
                        const statusMap: Record<string, string> = {
                            active: t('auction.status.active'),
                            sold: t('auction.status.sold'),
                            cancelled: t('auction.status.cancelled')
                        };
                        const statusText = statusMap[statusKey] ?? statusKey;
                        const statusClass = classNames({
                            'status-active': statusKey === 'active',
                            'status-sold': statusKey === 'sold',
                            'status-cancelled': statusKey === 'cancelled',
                        });
                        return (
                            <div
                                key={a.id}
                                style={{
                                    width: 300,
                                    border: '1px solid #eee',
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    background: '#fff',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                }}
                            >
                                <div style={{ width: '100%', height: 200, overflow: 'hidden', userSelect: 'none' }}>
                                    <img
                                        src={imgSrc}
                                        alt={a.court_name}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                    />
                                </div>

                                <div style={{ padding: 12 }}>
                                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>{a.court_name}</div>
                                    <div style={{ color: '#666', marginBottom: 6 }}>
                                        {formattedDate} {timeRange}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ color: '#444' }}>{a.court_type}</div>
                                        <div className="goods-price-count">
                                            <div className="goods-price">
                                                {a.price.toLocaleString()}
                                                <span className="goods-price-unit">&nbsp;円</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className={statusClass} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, fontSize: 12 }}>
                                        <div>{t('auction.sellerLabel')}: {a.seller_uname ?? '-'}</div>
                                        <div className="status">{t('auction.statusLabel')}: {statusText}</div>
                                    </div>
                                    {/* 購入ボタン */}
                                    {statusText !== '販売済み' && (
                                        <div style={{ marginTop: 12, textAlign: 'right' }}>
                                            <button
                                                onClick={() => handleBuy(a)}
                                                style={{
                                                    background: '#1976d2',
                                                    color: '#fff',
                                                    border: 'none',
                                                    padding: '6px 12px',
                                                    borderRadius: 4,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {t('auction.buyOneClick')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}